import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cartLineToItem, unitPrice, useCart } from '../store/cart';
import { CURRENCY, byId } from '../data/catalog';
import { Badge, Button, Card, Money, inputCls } from '../components/ui';
import { useTitle } from '../components/Layout';

/**
 * refund — rekomendowane zdarzenie GA4. Zawsze z jawnym `items[]` niosącym
 * DOKŁADNĄ ilość zwracaną w TEJ akcji — celowo nie korzystamy ze skrótu
 * "brak items = zwrot całej transakcji": zamówienie może być zwracane w
 * kilku osobnych krokach (najpierw część, potem reszta), więc "cała
 * transakcja" nigdy nie jest tu pewnym stwierdzeniem. `tax` i `shipping`
 * są świadomie pominięte — nie mam wiarygodnego sposobu policzenia, jaka
 * ich część przypada akurat na zwracane pozycje przy zwrocie częściowym,
 * a zgadywanie byłoby gorsze niż brak pola.
 */
function pushRefund(orderId: string, lines: Array<{ productId: string; sku: string; qty: number }>) {
  const items = lines
    .map((l) => cartLineToItem(l))
    .filter((item): item is NonNullable<typeof item> => item !== null);
  if (items.length === 0) return;

  const value = Math.round(items.reduce((s, it) => s + it.price * it.quantity, 0) * 100) / 100;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'refund',
    ecommerce: { transaction_id: orderId, currency: CURRENCY, value, items },
  });
}

export function Account() {
  useTitle('Moje konto');
  const cart = useCart();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [sent, setSent] = useState<string[]>([]);

  if (!cart.user) {
    return (
      <div className="mx-auto max-w-sm py-10">
        <h1 className="mb-4 text-xl font-semibold text-white">Zaloguj się</h1>
        <Card className="p-5">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!email.includes('@')) {
                setError('Podaj poprawny adres e-mail.');
                return;
              }
              setError('');
              const u = cart.login(email);

              // Kolejność ma znaczenie. `cart.login()` woła setUser()
              // wewnątrz — to aktualizacja stanu Reacta, ASYNCHRONICZNA:
              // efekt w CartProvider, który normalnie synchronizuje user_id
              // do dataLayer, uruchomi się dopiero PO ponownym renderze,
              // czyli już PO tym, jak zdarzenie login zdąży polecieć.
              // Sprawdzone empirycznie: bez tego user_id był `undefined`
              // dokładnie w momencie zdarzenia login, mimo że chwilę
              // później i tak się pojawiał. `cart.login()` zwraca jednak
              // utworzonego użytkownika SYNCHRONICZNIE (return u), więc
              // wypychamy user_id ręcznie, w tym samym bloku, PRZED
              // zdarzeniem login — efekt w CartProvider i tak odpali się
              // chwilę później z tą samą wartością, co jest nieszkodliwe.
              //
              // login — rekomendowane zdarzenie GA4. `method` to jedyny
              // udokumentowany parametr tego zdarzenia. Ten sklep ma tylko
              // jedną drogę logowania (formularz e-mail/hasło) — bez OAuth,
              // bez logowania społecznościowego — więc wartość jest stała,
              // ale prawdziwa: to faktycznie jedyna metoda, jaka istnieje.
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ user_id: u.id });
              window.dataLayer.push({ event: 'login', method: 'email' });
            }}
          >
            <div>
              <label className="mb-1 block text-xs text-slate-400" htmlFor="login-email">
                E-mail
              </label>
              <input
                id="login-email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jan@example.com"
              />
              {error && <p className="mt-1 text-[11.5px] text-[#ffb0bb]">{error}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-400" htmlFor="login-pass">
                Hasło
              </label>
              <input id="login-pass" type="password" className={inputCls} placeholder="••••••••" defaultValue="demo" />
            </div>
            <Button type="submit" className="w-full">
              Zaloguj
            </Button>
            <p className="text-center text-[11.5px] text-slate-500">
              Sklep demonstracyjny — dowolny adres e-mail utworzy konto.
            </p>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-white">Moje konto</h1>
        <Badge tone={cart.user.tier === 'premium' ? 'good' : 'info'}>
          {cart.user.tier === 'premium' ? 'Klient premium' : 'Klient standard'}
        </Badge>
        <span className="text-[13px] text-slate-400">{cart.user.email}</span>
        <Button variant="ghost" className="ml-auto" onClick={cart.logout}>
          Wyloguj
        </Button>
      </div>

      <h2 className="mb-3 text-sm font-semibold text-white">Historia zamówień ({cart.orders.length})</h2>

      {cart.orders.length === 0 && (
        <Card className="p-8 text-center text-[13px] text-slate-500">
          Nie masz jeszcze zamówień.{' '}
          <Link className="text-[#9ec5ff] underline" to="/katalog">
            Przejdź do katalogu
          </Link>
        </Card>
      )}

      <div className="space-y-3">
        {cart.orders.map((o) => {
          const anyRefund = Object.values(o.refunded).some((v) => v > 0);
          return (
            <Card key={o.id} className="p-4">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[12.5px] text-slate-200">{o.id}</span>
                <span className="text-[11.5px] text-slate-500">
                  {new Date(o.createdAt).toLocaleString('pl-PL')}
                </span>
                {anyRefund && <Badge tone="warn">zwrot w toku</Badge>}
                {sent.includes(o.id) && <Badge tone="good">zgłoszenie wysłane</Badge>}
                <span className="ml-auto text-[13.5px] font-semibold text-white">
                  <Money v={o.total} />
                </span>
              </div>

              <div className="space-y-2">
                {o.lines.map((l) => {
                  const p = byId(l.productId);
                  const v = p?.variants.find((x) => x.sku === l.sku);
                  const already = o.refunded[l.sku] ?? 0;
                  const max = l.qty - already;
                  const key = o.id + l.sku;
                  return (
                    <div key={l.sku} className="flex flex-wrap items-center gap-2 text-[12.5px]">
                      <span className="min-w-[200px] flex-1">
                        <span className="text-slate-200">{p?.name}</span>
                        <span className="ml-2 text-slate-500">
                          {v?.label} · {l.qty} szt.
                        </span>
                        {already > 0 && <span className="ml-2 text-[#ffb0bb]">zwrócono {already}</span>}
                      </span>
                      <span className="text-slate-400">
                        <Money v={unitPrice(l) * l.qty} />
                      </span>
                      <label className="flex items-center gap-1.5 text-[11.5px] text-slate-500">
                        do zwrotu
                        <input
                          type="number"
                          min={0}
                          max={max}
                          value={qty[key] ?? 0}
                          disabled={max === 0}
                          onChange={(e) =>
                            setQty((s) => ({ ...s, [key]: Math.max(0, Math.min(max, Number(e.target.value))) }))
                          }
                          className="w-14 rounded border border-[#26304d] bg-[#0b1020] px-2 py-1 text-[12px] text-white outline-none disabled:opacity-40"
                        />
                      </label>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    const items = o.lines
                      .map((l) => ({ sku: l.sku, qty: qty[o.id + l.sku] ?? 0 }))
                      .filter((x) => x.qty > 0);
                    if (items.length === 0) return;
                    items.forEach((x) => cart.markRefund(o.id, x.sku, x.qty));
                    pushRefund(
                      o.id,
                      items.map((x) => ({ productId: o.lines.find((l) => l.sku === x.sku)!.productId, sku: x.sku, qty: x.qty })),
                    );
                    fetch('/api/refund', {
                      method: 'POST',
                      headers: { 'content-type': 'application/json' },
                      body: JSON.stringify({ orderId: o.id, items }),
                    }).catch(() => {});
                    setQty({});
                    setSent((s) => [...s, o.id]);
                  }}
                >
                  Zgłoś zwrot zaznaczonych
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    o.lines.forEach((l) => cart.markRefund(o.id, l.sku, l.qty - (o.refunded[l.sku] ?? 0)));
                    pushRefund(
                      o.id,
                      o.lines
                        .map((l) => ({ productId: l.productId, sku: l.sku, qty: l.qty - (o.refunded[l.sku] ?? 0) }))
                        .filter((x) => x.qty > 0),
                    );
                    fetch('/api/refund', {
                      method: 'POST',
                      headers: { 'content-type': 'application/json' },
                      body: JSON.stringify({ orderId: o.id, full: true }),
                    }).catch(() => {});
                    setSent((s) => [...s, o.id]);
                  }}
                >
                  Zwróć całe zamówienie
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
}
